/** The only theme-wide settings a variant may change: read by the storefront's
 *  ProductPage, and by nothing the render-blocking /theme.css is built from. The Go
 *  test `TestGlobalPatchKeysMatchTheTypeScriptApplier` reads this literal. */
export const EXPERIMENT_GLOBAL_KEYS = ["product.sticky_atc", "product.show_low_stock", "product.trust_badges", "product.sale_timer", "product.sale_timer_minutes", "product.sale_timer_text"];
/** The section array an inserted section joins. Product and collection use their
 *  "default" template — the one every product renders unless it names another. */
function patchList(doc, list) {
    if (list === "header" || list === "footer")
        return doc.layout?.[list];
    const p = doc.pages;
    const page = p?.[list];
    return page?.templates ? page.templates.default : page?.sections;
}
function eachSection(doc, fn) {
    const visit = (list) => {
        for (const s of list ?? []) {
            if (fn(s))
                return s;
            const c = visit(s.children);
            if (c)
                return c;
        }
    };
    for (const l of ["header", "footer", "home", "page", "cart", "product", "collection"]) {
        const found = l === "product" || l === "collection"
            ? Object.values(doc.pages?.[l]?.templates ?? {}).map(visit).find(Boolean)
            : visit(patchList(doc, l));
        if (found)
            return found;
    }
}
function findBlockDeep(list, id) {
    for (const b of list ?? []) {
        if (b.id === id)
            return b;
        const c = findBlockDeep(b.children, id);
        if (c)
            return c;
    }
}
const unsafe = (k) => k === "__proto__" || k === "constructor" || k === "prototype";
/** Apply a patch to `doc` IN PLACE; returns how many ops found their target.
 *  The caller passes a copy — the storefront's cached payload is never mutated. */
export function applyExperimentPatch(doc, patch) {
    let hit = 0;
    for (const op of patch?.ops ?? []) {
        if ("global" in op) {
            const [group, key] = op.global.split(".");
            if (!EXPERIMENT_GLOBAL_KEYS.includes(op.global) || !group || !key)
                continue;
            const root = (doc.settings ??= {});
            const s = (root[group] ??= {});
            s[key] = op.value;
            hit++;
        }
        else if ("insert" in op) {
            const list = patchList(doc, op.list);
            const at = op.after ? (list?.findIndex((s) => s.id === op.after) ?? -1) + 1 : 0;
            // No anchor (deleted since) or the id already present: skip, never guess.
            if (!list || (op.after && at === 0) || eachSection(doc, (s) => s.id === op.insert.id))
                continue;
            list.splice(at, 0, JSON.parse(JSON.stringify(op.insert)));
            hit++;
        }
        else {
            const sec = eachSection(doc, (s) => s.id === op.section);
            const target = op.block ? findBlockDeep(sec?.blocks, op.block) : sec;
            if (!target)
                continue;
            if ("visible" in op)
                target.visible = op.visible;
            else {
                if (unsafe(op.key))
                    continue;
                const settings = { ...target.settings };
                if (op.value === null)
                    delete settings[op.key];
                else
                    settings[op.key] = op.value;
                target.settings = settings;
            }
            hit++;
        }
    }
    return hit;
}
// ---- deriving a patch from a builder session (dashboard only) ----------------
const LISTS = ["header", "footer", "home", "page", "cart", "product", "collection"];
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
function flatBlocks(list, parent, out) {
    ;
    (list ?? []).forEach((b, i) => {
        out.set(b.id, { b, at: `${parent}/${i}` });
        flatBlocks(b.children, b.id, out);
    });
    return out;
}
function allIds(doc) {
    const ids = new Set();
    eachSection(doc, (s) => void ids.add(s.id));
    return ids;
}
/** Leaf paths under two settings objects whose values differ. */
function changedPaths(a, b, prefix, out) {
    const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
    if (isObj(a) && isObj(b)) {
        for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
            changedPaths(a[k], b[k], prefix ? `${prefix}.${k}` : k, out);
        }
    }
    else if (!same(a, b))
        out.push(prefix);
}
/**
 * The patch that turns `baseline` into `edited`, or the reasons it cannot.
 *
 * - `published` is the document shoppers see, exactly as the server sent it. It
 *   decides which section ids EXIST on the storefront: the builder materialises a
 *   few sections on load (a header, a footer, main-product, main-cart) that the
 *   published doc may not carry, and an edit to one of those has to travel as an
 *   inserted section, not as a setting change addressed to an id no shopper has.
 * - `baseline` is the builder's document right after it loaded `published` and ran
 *   its load-time migrations — so those migrations are not mistaken for edits.
 * - `edited` is the document the seller made.
 *
 * Only what a patch can express is accepted: setting changes, show/hide, sections
 * added to a page, and the allowlisted product settings. Moving sections, adding or
 * removing blocks, and theme-wide settings (colours, fonts, layout) are refused
 * with a reason rather than saved and silently dropped (plan §3.7).
 */
export function diffExperimentPatch(published, baseline, edited) {
    const ops = [];
    const refusals = [];
    const live = allIds(published);
    const baseIds = allIds(baseline);
    // Theme-wide settings.
    const paths = [];
    changedPaths(baseline.settings, edited.settings, "", paths);
    const globals = new Set();
    for (const p of paths) {
        const key = EXPERIMENT_GLOBAL_KEYS.find((k) => p === k || p.startsWith(k + "."));
        if (key)
            globals.add(key);
        else {
            const group = p.split(".")[0] || p;
            if (!refusals.some((r) => r.setting === group)) {
                refusals.push({ reason: `Theme settings “${group}” apply to the whole store and cannot be tested — undo that change to save this version.`, setting: group });
            }
        }
    }
    for (const key of globals) {
        const [g, k] = key.split(".");
        ops.push({ global: key, value: edited.settings[g]?.[k] ?? null });
    }
    // Sections, list by list.
    for (const name of LISTS) {
        const templ = name === "product" || name === "collection";
        const b = patchList(baseline, name) ?? [];
        const e = patchList(edited, name) ?? [];
        if (templ) {
            // Only the default template can take an insert; other templates' sections
            // are still reached by id through the per-section diff below.
            const bt = baseline.pages[name]?.templates ?? {};
            const et = edited.pages[name]?.templates ?? {};
            for (const t of Object.keys({ ...bt, ...et })) {
                if (t !== "default")
                    diffSections(bt[t] ?? [], et[t] ?? [], null);
            }
        }
        diffSections(b, e, name);
    }
    function anchor(list, i, inserted) {
        for (let j = i - 1; j >= 0; j--) {
            const id = list[j].id;
            if (live.has(id) || inserted.has(id))
                return id;
        }
        return "";
    }
    function diffSections(b, e, list) {
        const bById = new Map(b.map((s) => [s.id, s]));
        const eById = new Map(e.map((s) => [s.id, s]));
        // Order among the sections that are live and still present.
        const order = (l, other) => l.filter((s) => live.has(s.id) && other.has(s.id)).map((s) => s.id).join();
        if (order(b, eById) !== order(e, bById)) {
            refusals.push({ reason: "Moving sections can't be tested yet — hide a section and add one where you want it instead." });
        }
        const inserted = new Set();
        const editedIds = allIds(edited);
        e.forEach((s, i) => {
            const was = bById.get(s.id);
            if (!was && baseIds.has(s.id)) {
                refusals.push({ reason: "Moving a section to another page can't be tested.", section: s.id });
            }
            else if (!live.has(s.id)) {
                // Added by the seller, or materialised by the builder and then edited.
                if (was && same(was, s))
                    return;
                if (!list) {
                    refusals.push({ reason: "Adding a section to a template other than the default can't be tested.", section: s.id });
                    return;
                }
                ops.push({ insert: JSON.parse(JSON.stringify(s)), list, after: anchor(e, i, inserted) });
                inserted.add(s.id);
            }
            else if (was) {
                diffOne(was, s);
            }
        });
        for (const s of b) {
            if (live.has(s.id) && !eById.has(s.id) && !editedIds.has(s.id))
                ops.push({ section: s.id, visible: false });
        }
    }
    function diffOne(b, e) {
        if (b.visible !== e.visible)
            ops.push({ section: e.id, visible: e.visible });
        for (const k of new Set([...Object.keys(b.settings ?? {}), ...Object.keys(e.settings ?? {})])) {
            if (!same(b.settings?.[k], e.settings?.[k]))
                ops.push({ section: e.id, key: k, value: e.settings?.[k] ?? null });
        }
        const bb = flatBlocks(b.blocks, "", new Map());
        const eb = flatBlocks(e.blocks, "", new Map());
        const shape = (m) => [...m].map(([id, v]) => `${id}@${v.at}`).sort().join();
        if (shape(bb) !== shape(eb)) {
            refusals.push({ reason: "Adding, removing or moving blocks can't be tested yet — hide a block or change its settings instead.", section: e.id });
        }
        else {
            for (const [id, { b: was }] of bb) {
                const now = eb.get(id).b;
                if (was.visible !== now.visible)
                    ops.push({ section: e.id, block: id, visible: now.visible });
                for (const k of new Set([...Object.keys(was.settings ?? {}), ...Object.keys(now.settings ?? {})])) {
                    if (!same(was.settings?.[k], now.settings?.[k]))
                        ops.push({ section: e.id, block: id, key: k, value: now.settings?.[k] ?? null });
                }
            }
        }
        // A section-group's children: same rules, one level (the schema's cap).
        if (b.children || e.children) {
            const bc = b.children ?? [];
            const ec = e.children ?? [];
            if (bc.map((s) => s.id).join() !== ec.map((s) => s.id).join()) {
                refusals.push({ reason: "Adding, removing or moving sections inside a group can't be tested yet.", section: e.id });
            }
            else
                bc.forEach((c, i) => diffOne(c, ec[i]));
        }
    }
    return { ops, refusals };
}
//# sourceMappingURL=experiment-patch.js.map